import React, { useEffect, useMemo, useState } from "react";
import { useLazyQuery } from "@apollo/client";
import { Box, TextField, Typography } from "@mui/material";
import { pricingCalculator } from "types/gql-types";
import { PricingFormType } from "./form";
import { buildPricingCalculatorInput } from "./pricing-form-utils";
import { PRICING_CALCULATOR } from "./queries";

const PRICING_TYPE_FLAT_ID = 1;

const formatMoney = (value: string | number): string => {
  const amount = typeof value === "number" ? value : parseFloat(String(value));
  if (Number.isNaN(amount)) return "$0.00";
  return `$${amount.toFixed(2)}`;
};

type Props = {
  values: PricingFormType;
}

export const PricingCalculator: React.FC<Props> = ({ values }) => {
  const [unitCountInput, setUnitCountInput] = useState("");
  const [runCalculator, { data, loading, error }] = useLazyQuery<pricingCalculator>(PRICING_CALCULATOR);

  const unitCount = useMemo(() => {
    const parsed = parseInt(unitCountInput, 10);
    return Number.isNaN(parsed) || parsed < 0 ? null : parsed;
  }, [unitCountInput]);

  const calculatorInput = useMemo(() => buildPricingCalculatorInput(values), [values]);

  useEffect(() => {
    if (unitCount === null) return undefined;

    const timeoutId = window.setTimeout(() => {
      void runCalculator({
        variables: {
          unitCount,
          pricing: calculatorInput,
        },
      });
    }, 300);

    return () => window.clearTimeout(timeoutId);
  }, [unitCount, calculatorInput, runCalculator]);

  const result = data?.pricingCalculator;
  const isFlat = values.pricingTypeId === PRICING_TYPE_FLAT_ID;

  const annualDiscountPercent = values.annualDiscountPercent ?? 0;
  const hasAnnualDiscount = annualDiscountPercent > 0;
  const discountMultiplier = 1 - annualDiscountPercent / 100;

  const toNumber = (value: string | number | null | undefined): number => {
    if (value == null) return Number.NaN;
    return typeof value === "number" ? value : parseFloat(String(value));
  };

  const grandTotalNum = result ? toNumber(result.grandTotal) : Number.NaN;
  const discountAmount = Number.isNaN(grandTotalNum) ? Number.NaN : grandTotalNum * (annualDiscountPercent / 100);
  const avgPerUnitNum = result ? toNumber(result.averagePerUnit) : Number.NaN;
  const finalPerUnitAnnual = Number.isNaN(avgPerUnitNum) ? Number.NaN : avgPerUnitNum * discountMultiplier;
  const finalTotalAnnual = Number.isNaN(grandTotalNum) ? Number.NaN : grandTotalNum * discountMultiplier;

  return (
    <Box className="card" sx={{ p: 2, height: "fit-content" }}>
      <Typography variant="h6" component="h4" gutterBottom>
        Pricing calculator
      </Typography>

      <TextField
        fullWidth
        margin="normal"
        label="Number of units"
        type="number"
        value={unitCountInput}
        onChange={event => setUnitCountInput(event.target.value)}
        inputProps={{ min: 0, step: 1, "aria-label": "Calculator unit count" }}
      />

      {unitCount === null ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Enter a unit count to preview monthly cost.
        </Typography>
      ) : null}

      {loading ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Calculating…
        </Typography>
      ) : null}

      {error ? (
        <Typography variant="body2" color="error" sx={{ mt: 2 }}>
          {error.message}
        </Typography>
      ) : null}

      {result && unitCount !== null ? (
        <Box sx={{ mt: 2 }}>
          {isFlat ? (
            <>
              <Typography variant="body2">
                Flat monthly price: {formatMoney(result.grandTotal)}
              </Typography>

              {hasAnnualDiscount ? (
                <>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    Annual discount ({annualDiscountPercent}%): &minus;{formatMoney(discountAmount)}/month
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    Final monthly price (paid annually): {formatMoney(finalTotalAnnual)}/month
                  </Typography>
                </>
              ) : null}
            </>
          ) : (
            <>
              {!result.thresholdMet ? (
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Threshold not met.
                </Typography>
              ) : null}

              {result.thresholdMet && result.minimumIgnored ? (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="body2">Threshold met.</Typography>
                  <Typography variant="body2">Minimum price ignored.</Typography>
                </Box>
              ) : null}

              {result.thresholdMet ? result.lines.map((line, index) => (
                <Typography key={`calc-line-${index}`} variant="body2" sx={{ mb: 0.5 }}>
                  {formatMoney(line.rate)} &times; {line.unitCount} = {formatMoney(line.subtotal)}
                </Typography>
              )) : null}

              <Typography variant="body1" fontWeight={700} sx={{ mt: 1.5 }}>
                Grand Total: {formatMoney(result.grandTotal)}/month
              </Typography>

              {result.thresholdMet && result.minimumApplied ? (
                <Typography variant="caption" color="text.secondary" display="block">
                  Minimum monthly charge applied.
                </Typography>
              ) : null}

              {result.averagePerUnit != null && unitCount > 0 ? (
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  Divided by {unitCount} units = {formatMoney(result.averagePerUnit)}/unit/month
                </Typography>
              ) : null}

              {hasAnnualDiscount ? (
                <>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    Annual discount ({annualDiscountPercent}%): &minus;{formatMoney(discountAmount)}/month
                  </Typography>

                  {result.averagePerUnit != null && unitCount > 0 ? (
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      Final price per unit per month (paid annually):{" "}
                      {formatMoney(finalPerUnitAnnual)}/unit/month
                    </Typography>
                  ) : null}
                </>
              ) : null}
            </>
          )}
        </Box>
      ) : null}
    </Box>
  );
};
